import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { Search } from "lucide-react";

export const CONSULTATION_STATUSES = ["New", "Contacted", "Scheduled", "Closed"];

const CONSULT_BADGE = {
  "New": "bg-brand-teal/10 text-brand-teal",
  "Contacted": "bg-blue-100 text-blue-800",
  "Scheduled": "bg-green-100 text-green-800",
  "Closed": "bg-slate-200 text-slate-600",
};

export const ConsultBadge = ({ value }) => (
  <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold whitespace-nowrap ${CONSULT_BADGE[value] || "bg-slate-200 text-slate-600"}`}>{value}</span>
);

export default function Consultations() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("newest");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/admin/consultations").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    let list = [...items];
    if (q) {
      const needle = q.toLowerCase();
      list = list.filter((c) => [c.name, c.phone, c.email, c.care_for].filter(Boolean).join(" ").toLowerCase().includes(needle));
    }
    if (status) list = list.filter((c) => c.status === status);
    if (sort === "oldest") list.sort((a, b) => (a.submitted_at || "").localeCompare(b.submitted_at || ""));
    else if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    else list.sort((a, b) => (b.submitted_at || "").localeCompare(a.submitted_at || ""));
    return list;
  }, [items, q, status, sort]);

  if (!items) return <p className="text-slate-500 font-semibold">Loading consultation requests…</p>;

  return (
    <div data-testid="admin-consultations">
      <h1 className="font-display text-3xl md:text-4xl text-brand-navy">Consultations</h1>
      <p className="mt-2 text-[15px] text-slate-600">Free care consultation requests submitted through the public website.</p>

      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            data-testid="consultation-search"
            type="text"
            placeholder="Search by name, phone, or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full rounded-xl border border-slate-300 bg-white pl-11 pr-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-teal/60 focus:border-brand-teal"
          />
        </div>
        <select data-testid="consultation-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-teal/60">
          <option value="">All statuses</option>
          {CONSULTATION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="consultation-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-teal/60">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="name">Name A–Z</option>
        </select>
      </div>

      <p className="mt-4 text-sm font-semibold text-slate-500">{filtered.length} request{filtered.length === 1 ? "" : "s"}</p>

      <div className="mt-4 hidden md:block rounded-2xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-left text-sm min-w-[800px]">
          <thead>
            <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3 font-bold">Name</th>
              <th className="px-4 py-3 font-bold">Submitted</th>
              <th className="px-4 py-3 font-bold">Phone</th>
              <th className="px-4 py-3 font-bold">Care For</th>
              <th className="px-4 py-3 font-bold">Preferred Date</th>
              <th className="px-4 py-3 font-bold">Preferred Time</th>
              <th className="px-4 py-3 font-bold">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr
                key={c.id}
                data-testid={`consultation-row-${c.id}`}
                onClick={() => navigate(`/portal/consultations/${c.id}`)}
                className="border-b border-slate-100 hover:bg-brand-mist/40 cursor-pointer transition-colors duration-200"
              >
                <td className="px-4 py-3 font-bold text-brand-navy">{c.name}</td>
                <td className="px-4 py-3">{new Date(c.submitted_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">{c.phone}</td>
                <td className="px-4 py-3">{c.care_for || "—"}</td>
                <td className="px-4 py-3">{c.preferred_date}</td>
                <td className="px-4 py-3">{c.preferred_time}</td>
                <td className="px-4 py-3"><ConsultBadge value={c.status} /></td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No consultation requests match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 md:hidden space-y-3">
        {filtered.map((c) => (
          <button
            key={c.id}
            data-testid={`consultation-card-${c.id}`}
            onClick={() => navigate(`/portal/consultations/${c.id}`)}
            className="w-full text-left rounded-2xl border border-slate-200 bg-white p-5"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-bold text-brand-navy">{c.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{new Date(c.submitted_at).toLocaleDateString()} · {c.phone}</p>
              </div>
              <ConsultBadge value={c.status} />
            </div>
            <p className="mt-2 text-sm text-slate-600">{c.preferred_date} · {c.preferred_time}</p>
          </button>
        ))}
        {filtered.length === 0 && <p className="text-center text-slate-500 py-10">No consultation requests match your filters.</p>}
      </div>
    </div>
  );
}
